import type { HeroClass } from './heroes.ts';

/**
 * Damage types (docs/CRIATURAS.md §3). Every blow has one: physical unless its source says otherwise.
 * Holy only ever comes from heroes.
 */
export type DamageType = 'physical' | 'fire' | 'frost' | 'lightning' | 'acid' | 'poison' | 'shadow' | 'holy';
export const DAMAGE_TYPES: DamageType[] = ['physical', 'fire', 'frost', 'lightning', 'acid', 'poison', 'shadow', 'holy'];

/** The ones gear can guard against (affinity), each with its opposite. */
export type AffinityType = 'fire' | 'frost' | 'lightning' | 'acid' | 'poison' | 'shadow';
export const AFFINITY_TYPES: AffinityType[] = ['fire', 'frost', 'lightning', 'acid', 'poison', 'shadow'];
export const OPPOSITE: Record<AffinityType, AffinityType> = { fire: 'frost', frost: 'fire', lightning: 'acid', acid: 'lightning', poison: 'shadow', shadow: 'poison' };

/**
 * Affinity on armor and amulets: a share of the roll (`attrBp`, rolls run higher than other attributes)
 * guards against its type, and half of that is lost against the opposite one. A hero's total stays
 * between `minBp` and `maxBp`.
 */
export const AFFINITY = { attrBp: 25000, oppositeBp: 5000, maxBp: 7500, minBp: -5000 };

/**
 * Conditions that last days (docs/CRIATURAS.md §5): only from bosses, only on heroes who fought live (a
 * ghost never catches one), one at a time. The ash curse (the witch felling a hero) takes `hpBp` off its
 * health; dread (an elite giant felling it) starts every fight frightened for `fearTicks`. The crypt and
 * incursion bosses leave theirs with their marked blow (`AILMENT_MARKS`): grave dust (the Warden) slows
 * attacks, cinders (the magma lord, and the Warden in embers) open the hero to fire, glass blood (the
 * colossus) to blows.
 */
export type AilmentKind = 'ashCurse' | 'dread' | 'graveDust' | 'cinders' | 'glassBlood';
export const AILMENT_KINDS: AilmentKind[] = ['ashCurse', 'dread', 'graveDust', 'cinders', 'glassBlood'];
export const AILMENTS: Record<AilmentKind, { hours: number; hpBp?: number; fearTicks?: number; attackSpeedBp?: number; resBp?: Partial<Record<DamageType, number>> }> = {
  ashCurse: { hours: 24, hpBp: -1500 },
  dread: { hours: 24, fearTicks: 18 },
  graveDust: { hours: 12, attackSpeedBp: -1000 },
  cinders: { hours: 18, resBp: { fire: -1500 } },
  glassBlood: { hours: 18, resBp: { physical: -1000 } },
};
/**
 * A boss's marked blow (the Warden's slams and rings, the magma lord's eruptions, the colossus's slam)
 * leaves its condition on a live hero it catches `hits` times in one fight: dodging it is the answer. A
 * hero takes at most one new condition every `everyHours`, so a bad day never piles them up.
 */
export const AILMENT_MARKS = { hits: 2, everyHours: 24 };
/** Curing one: a cleric for free, once every `clericHours`, even for a friend who's away (and earns `clericXp` for a friend); or the temple, for dust from the storage. */
export const AILMENT_CURE = { clericHours: 24, templeDust: 150, clericXp: 150 };

/** The omen names a wave's damage type when that type brings at least this share of the wave's points. */
export const OMEN_TYPE_SHARE_BP = 2500;

/** What a hero's own blows are: the mage's arcane bolts are lightning, the cleric's and paladin's holy. */
export const HERO_DMG_TYPE: Partial<Record<HeroClass, DamageType>> = { mage: 'lightning', cleric: 'holy', paladin: 'holy' };

/** What each projectile and each ground zone deals (absent: physical; an enchanted shot deals its element). */
export const PROJECTILE_DMG_TYPE: Partial<Record<string, DamageType>> = { fireball: 'fire', orb: 'lightning', light: 'holy' };
export const ZONE_DMG_TYPE: Partial<Record<string, DamageType>> = {
  burn: 'fire',
  frost: 'frost',
  storm: 'lightning',
  sanctuary: 'holy',
  hallow: 'holy',
  lava: 'fire',
  eruption: 'fire',
  wyrmfire: 'fire',
};

/** Buildings by material (1 wood, 2 stone, 3 iron): wood burns, acid eats stone, iron holds against both. */
export const MATERIAL_RESIST: Record<1 | 2 | 3, Partial<Record<DamageType, number>>> = {
  1: { fire: -5000 },
  2: { fire: 2500, acid: -3000 },
  3: { fire: 3000, acid: 3000 },
};

/**
 * Reactions between damage types (docs/PODERES.md §4), so carrying things that go together pays:
 * - chill: frost damage leaves a nightmare chilled for `chillTicks` (a frost wall only slows: its bite chills);
 * - shatter: lightning on a chilled nightmare bursts it in ice: `damageBp` of the blow (at least `min`) to
 *   everything within `radius`, and the chill is gone;
 * - combust: fire on a poisoned nightmare (or one eaten by acid) sets the poison off: what was left of it
 *   (`damageBp`, at least `min`) to everything within `radius`, and the poison and acid are gone.
 * A reaction's own damage never sets off another.
 */
export const REACTIONS = {
  chillTicks: 36,
  shatter: { radius: 1200, damageBp: 8000, min: 20 },
  combust: { radius: 1500, damageBp: 10000, min: 30 },
};
